import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getMembers,
  inviteFailure,
  Project,
  type AnyEntity,
  type Participant,
  type ShareResult,
  type TypeId,
} from '@sdk';
import { useEntity } from '@src/hooks/entity-hooks';
import { useMembershipAvailability, type MembershipReason } from '@src/hooks/use-membership-availability';

/** Module-level stable empty result so callers' equality checks (and any
 *  downstream useMemo deps) don't churn while the entity is still loading. */
const EMPTY_MEMBERS: readonly Participant[] = Object.freeze([]);

/** Module-level in-flight promise cache keyed by typeId.toString() so two
 *  consumers (e.g. ConversationView + MembersAvatarStack) mounting against
 *  the same conversation share the single `getMembers` round-trip instead
 *  of double-fetching. The entry is evicted once the promise settles. */
const _inFlight = new Map<string, Promise<Participant[]>>();

function _sharedGetMembers(typeId: TypeId): Promise<Participant[]> {
  const key = typeId.toString();
  const existing = _inFlight.get(key);
  if (existing) return existing;
  const promise = getMembers(typeId).finally(() => {
    if (_inFlight.get(key) === promise) _inFlight.delete(key);
  });
  _inFlight.set(key, promise);
  return promise;
}

export interface UseMembersResult {
  /** The entity itself, already resolved here for the roster — exposed so
   *  callers needing it (e.g. to publish before minting an invite link) don't
   *  mount a second ``useEntity`` for the same typeId. */
  entity: AnyEntity | null | undefined;
  /** Members + roles. Sourced from the local entity cache, then refreshed
   *  on mount via the generic ``members`` action (which the local server
   *  reflects to the hub when ``entity.remote=true``). */
  members: Participant[];
  /** True once the hub fetch has resolved at least once for this typeId
   *  (success OR failure — both leave `loading=false` and prove the roster
   *  is no longer "unknown"), OR membership is unavailable (nothing to wait
   *  for). Distinct from `members.length > 0` which can't tell "empty roster"
   *  from "fetch hasn't returned yet". */
  ready: boolean;
  /** Last fetch error, or null. ``members`` still falls through to the
   *  entity cache on failure so the UI degrades gracefully. */
  error: Error | null;
  /** True while a refresh is in flight over an already-shown cache — the header
   *  shows "updating…". */
  updating: boolean;
  /** True when signed in but the last refresh FAILED (hub unreachable): the UI
   *  shows the cached roster + "can't update — showing last synced". Never a
   *  sign-in prompt (the user is already authenticated). */
  stale: boolean;
  /** False when membership can't be used on this client (not signed in, or Local
   *  mode). Surfaces such that every members surface can disable + branch. */
  available: boolean;
  /** Why membership is/ isn't available — drives sign-in vs Local-mode copy. */
  reason: MembershipReason;
  /** Re-fetch via ``getMembers``. Caller uses this after actions that may
   *  have changed membership (invite, leave, role change). */
  refresh: () => Promise<void>;
  /** Invite one or more members in a single ``share`` round-trip — the same
   *  collapsed shape ``APIEntity.share`` itself takes. ``idOrEmail`` is
   *  either an email or a hub user id (a contact the address book knows only
   *  by id, e.g. from a conversation roster, has no email to invite by);
   *  ``role`` is optional and only a Project invite honours it. Recipients
   *  only appear in ``members`` after they accept + join hub-side.
   *
   *  On a Project every invite goes through ``Project.invite`` and its
   *  ``ShareResult`` is returned: ``teams`` ride the SAME round-trip, each
   *  granted on the hub as one principal, and each person and team has its own
   *  outcome. It throws only when nothing landed; a partial failure is read
   *  from the result. Any other entity uses the plain ``share`` and returns null. */
  addMembers: (
    users: { idOrEmail: string; roles?: string[] }[],
    opts?: { teams?: TypeId[] },
  ) => Promise<ShareResult | null>;
  /** Remove a member by user id. OWNER ONLY — the hub rejects non-owner (and
   *  owner-self) callers with 403, which throws here. Refreshes after. */
  removeMember: (userId: string) => Promise<void>;
  /** Change a member's role. Gated hub-side by the role-grant chokepoint
   *  (``can_assign``: assign strictly below your rank, on a member strictly
   *  below your rank, never self/owner) — denials throw (403). ``role`` is a
   *  lowercase policy role (``admin``/``editor``/``member``/``reader``).
   *  Refreshes the roster after so the new role shows immediately. */
  setRole: (userId: string, roles: string[]) => Promise<void>;
}

/**
 * Generic members hook — works on any entity TypeId.
 *
 * Reads the initial member list from the locally-cached entity. On mount,
 * fires a single ``getMembers`` call to refresh from the canonical source
 * (the hub, when the entity is remote). No interval, no focus auto-refresh.
 * Multiple consumers for the same typeId share an in-flight fetch.
 */
export function useMembers(typeId: TypeId | null): UseMembersResult {
  const { data: entity } = useEntity<AnyEntity>(typeId);
  const { available, reason } = useMembershipAvailability();
  const [refreshed, setRefreshed] = useState<Participant[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    // Membership is 100% hub-driven — with no hub there is nothing to refresh.
    if (!typeId || !available) return;
    setLoading(true);
    try {
      const fresh = await _sharedGetMembers(typeId);
      setRefreshed(fresh);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [typeId, available]);

  // Cancel-on-unmount + cancel-on-typeId-change: a late-arriving response
  // for typeId A must NOT call setRefreshed against typeId B (cross-tenant
  // roster bleed). The cancelled flag closes over the effect run, so the
  // setter no-ops once the cleanup ran.
  useEffect(() => {
    // Skip the fetch entirely when there's no typeId or no hub — don't even
    // touch the shared in-flight map. An unavailable state clears any prior
    // fetch so a sign-out immediately drops to the disabled surface.
    if (!typeId || !available) {
      setRefreshed(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    _sharedGetMembers(typeId)
      .then((fresh) => {
        if (cancelled) return;
        setRefreshed(fresh);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [typeId, available]);

  // The stored roster changes when the hub pushes one (a member left on another device, a ticket
  // was returned): read the membership again so the view follows without a manual refresh.
  const storedRoster = JSON.stringify((entity as { members?: unknown } | undefined)?.members ?? null);
  const seenRoster = useRef(storedRoster);
  useEffect(() => {
    if (seenRoster.current === storedRoster) return;
    seenRoster.current = storedRoster;
    void refresh();
  }, [storedRoster, refresh]);

  const addMembers = useCallback(
    async (users: { idOrEmail: string; roles?: string[] }[], opts?: { teams?: TypeId[] }) => {
      const teams = opts?.teams ?? [];
      if (!users.length && !teams.length) return null;
      if (!entity) throw new Error('useMembers: entity not loaded; cannot invite');
      if (entity instanceof Project) {
        const result = await entity.invite(users, teams.length ? { teams } : {});
        await refresh();
        const failure = inviteFailure(result);
        if (failure) throw failure;
        return result;
      }
      if (teams.length) throw new Error('useMembers: only a project can be shared with a team');
      // One hub MembershipRequest per invitee; the desktop publishes the entity first when needed.
      // Every invitee is tried even when one fails, and the roster refreshes either way.
      try {
        const results = await Promise.allSettled(users.map((user) => entity.inviteMember(user.idOrEmail, user.roles)));
        const failed = users.filter((_, i) => results[i].status === 'rejected');
        if (failed.length) {
          const reason = (results.find((r) => r.status === 'rejected') as PromiseRejectedResult).reason;
          const detail = reason instanceof Error ? reason.message : String(reason);
          throw new Error(`Could not invite ${failed.map((u) => u.idOrEmail).join(', ')}: ${detail}`);
        }
      } finally {
        await refresh();
      }
      return null;
    },
    [entity, refresh],
  );

  // Remove a member by user id. OWNER ONLY — the hub returns 403 for a
  // non-owner caller (or owner-self), which propagates as a thrown error here;
  // callers should surface it. Refreshes the roster after so the removed user
  // drops out of the list.
  const removeMember = useCallback(
    async (userId: string) => {
      const trimmed = userId.trim();
      if (!trimmed) return;
      if (!entity) throw new Error('useMembers: entity not loaded; cannot remove');
      await (entity as any).removeMember(trimmed);
      await refresh();
    },
    [entity, refresh],
  );

  // Change a member's role by user id. The hub's ``can_assign`` gate rejects
  // out-of-ceiling callers with 403, which propagates as a thrown error here;
  // callers should surface it. Refreshes the roster after so the row shows
  // the new role without a manual reload.
  const setRole = useCallback(
    async (userId: string, roles: string[]) => {
      const trimmedId = userId.trim();
      const trimmedRoles = roles.map(role => role.trim().toLowerCase());
      if (!trimmedId || !trimmedRoles.length) return;
      if (!entity) throw new Error('useMembers: entity not loaded; cannot change role');
      await (entity as any).setMemberRole(trimmedId, trimmedRoles);
      await refresh();
    },
    [entity, refresh],
  );

  // Stale-while-revalidate: the HUB answer wins the moment it arrives. Until
  // then, paint the locally-cached roster (``Entity.members``, kept warm by the
  // fanout for conversations and by the last reflect for everything else) so the
  // list shows instantly. On a refresh failure we show the cache (stale) rather
  // than blanking — and rather than an earlier hub answer: the read can fail
  // because the caller is no longer in it (desk staff who returned a ticket may
  // not read its roster), and the pushed roster is then newer than our last read.
  // This is the inverse of the old "cache wins when non-empty" rule — a roster
  // the hub has since emptied now correctly clears.
  const cached = (entity as any)?.members as Participant[] | undefined;
  const cachedMembers = Array.isArray(cached) ? cached : null;
  const fresh = error === null ? refreshed : null;
  const members: Participant[] = fresh ?? cachedMembers ?? refreshed ?? (EMPTY_MEMBERS as Participant[]);
  // "updating…" while a refresh runs over an already-shown cache (the internal
  // ``loading`` state is exposed under the SWR-flavored name).
  const updating = loading;
  // Signed in but the refresh failed → show cached + "can't update".
  const stale = available && error !== null;
  // `ready` is "the hub has answered at least once" (success OR failure), OR
  // membership is unavailable (nothing to wait for) — so UI gating on
  // rosterReady never stalls on a logged-out / offline surface.
  const ready = useMemo(() => refreshed !== null || error !== null || !available, [refreshed, error, available]);

  return {
    entity,
    members,
    ready,
    error,
    updating,
    stale,
    available,
    reason,
    refresh,
    addMembers,
    removeMember,
    setRole,
  };
}
